import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import Navbar from "@/components/Navbar";

export default function Discover() {
  const [users, setUsers] = useState([]);
  const [idx, setIdx] = useState(0);
  const [radius, setRadius] = useState(0); // 0 = no filter
  const [loading, setLoading] = useState(true);

  const load = async (r) => {
    setLoading(true);
    try {
      const params = r ? { radius_km: r } : {};
      const { data } = await api.get("/discover", { params });
      setUsers(data.users);
      setIdx(0);
    } catch (e) {
      toast.error("Couldn't load profiles");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const current = users[idx];

  const rate = async (score) => {
    if (!current) return;
    try {
      const { data } = await api.post("/rate", { target_user_id: current.id, score });
      if (data.is_match) toast.success("🔥 IT'S A MATCH — check Matches");
      else toast.success(`Rated ${score}/10`);
      setIdx(i => i + 1);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rating failed");
    }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="font-mono text-xs uppercase tracking-widest text-[#555]">[ RATE THE DECK ]</div>
            <h1 className="font-display text-4xl tracking-tighter mt-1">Editorial<br />casting.</h1>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-mono text-xs">RADIUS:</span>
            {[0, 5, 25, 100].map(r => (
              <button
                key={r}
                data-testid={`radius-${r}`}
                onClick={() => { setRadius(r); load(r || undefined); }}
                className={`px-3 py-1 border border-black font-mono text-xs ${radius === r ? "bg-black text-white" : "hover:bg-black hover:text-white"}`}
              >
                {r === 0 ? "ANY" : `${r}KM`}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="brutal bg-white p-20 text-center font-mono text-sm">LOADING DECK...</div>
        ) : !current ? (
          <div data-testid="empty-deck" className="brutal bg-white p-20 text-center">
            <div className="font-display text-3xl mb-3">DECK EMPTY.</div>
            <p className="text-sm text-[#555]">You've seen everyone within range. Try a wider radius or come back later.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            <div className="lg:col-span-7" data-testid="rate-card">
              <div className="brutal brutal-shadow bg-white overflow-hidden relative">
                {current.boosted && (
                  <div className="absolute top-3 left-3 z-10 px-2 py-1 bg-[#E53935] text-white font-mono text-[10px] tracking-widest" data-testid="boosted-badge">
                    ★ BOOSTED
                  </div>
                )}
                <img src={current.photo_base64} alt={current.name} className="w-full aspect-[4/5] object-cover" />
                <div className="p-5 border-t border-black flex items-end justify-between">
                  <div>
                    <div className="font-display text-3xl tracking-tighter">{current.name.toUpperCase()}, {current.age}</div>
                    <div className="font-mono text-xs text-[#555] mt-1">
                      {current.city || "—"}{current.distance_km != null ? ` • ${current.distance_km} KM` : ""}
                    </div>
                    {current.bio && <p className="mt-3 text-sm max-w-md">{current.bio}</p>}
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-[10px] text-[#555]">CURRENT AVG</div>
                    <div className="font-mono font-bold text-2xl">
                      {current.community_rating ?? "—"}<span className="text-sm text-[#555]"> / 10</span>
                    </div>
                    <div className="font-mono text-[10px] text-[#555]">{current.community_votes || 0} VOTES</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="brutal bg-white p-6 sticky top-24">
                <div className="font-mono text-xs uppercase mb-4">SCORE THIS FACE — 1 to 10</div>
                <div className="grid grid-cols-5 gap-2">
                  {[1,2,3,4,5,6,7,8,9,10].map(n => (
                    <button
                      key={n}
                      data-testid={`rate-btn-${n}`}
                      onClick={() => rate(n)}
                      className="rating-btn brutal aspect-square text-xl bg-white"
                    >
                      {n}
                    </button>
                  ))}
                </div>
                <p className="mt-4 font-mono text-[10px] text-[#555] leading-snug">
                  RATING ≥ 7 EITHER WAY = MUTUAL CONSIDERATION.<br />
                  IF BOTH RATE ≥ 7 → MATCH CREATED.
                </p>
                <button
                  data-testid="rate-skip"
                  onClick={() => setIdx(i => i + 1)}
                  className="mt-6 w-full py-3 border border-black font-mono text-xs hover:bg-black hover:text-white"
                >
                  SKIP →
                </button>
                <div className="mt-6 font-mono text-[10px] text-[#555] flex justify-between">
                  <span>POSITION {idx + 1}/{users.length}</span>
                  <span>AI: {current.ai_rating ?? "—"}</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
